import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useAuth } from '@/auth/AuthContext'
import { saveLastRoute } from '@/auth/routeMemory'
import {
  canAccessAdminSettings,
  canAccessManagementAreas,
  canAccessOrganisations,
  canReviewApprovals,
} from '@/lib/roles'
import { useTheme } from '@/theme/ThemeContext'
import { initialsFromName } from '@/utils/helpers'

type NavLeaf = {
  to: string
  label: string
  end?: boolean
  icon: ReactNode
}

type NavFlyoutGroup = {
  label: string
  items: NavLeaf[]
}

type NavFlyout = {
  id: string
  label: string
  icon: ReactNode
  children?: NavLeaf[]
  groups?: NavFlyoutGroup[]
}

type NavEntry = NavLeaf | NavFlyout

function isFlyout(entry: NavEntry): entry is NavFlyout {
  return 'id' in entry && ('children' in entry || 'groups' in entry)
}

function flyoutLeaves(entry: NavFlyout): NavLeaf[] {
  if (entry.groups?.length) return entry.groups.flatMap((g) => g.items)
  return entry.children ?? []
}

const I = {
  dashboard: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3.5" y="3.5" width="7.5" height="7.5" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <rect x="13" y="3.5" width="7.5" height="4.5" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <rect x="13" y="10.5" width="7.5" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <rect x="3.5" y="13.5" width="7.5" height="7" rx="2" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  ),
  referrals: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M8 7h8M8 12h8M8 17h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  ),
  calendar: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3.5" y="5" width="17" height="15" rx="3" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 3.5v3M16 3.5v3M3.5 9.5h17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  roster: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="9" cy="8.5" r="3" stroke="currentColor" strokeWidth="1.6" />
      <path d="M4.5 18.5c.7-2.5 2.6-3.8 4.5-3.8s3.8 1.3 4.5 3.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="16.5" cy="9" r="2.4" stroke="currentColor" strokeWidth="1.6" />
      <path d="M15.2 14.2c1.5.4 2.7 1.4 3.3 3.3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  clock: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6" />
      <path d="M12 8v4.2l2.8 1.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  capacity: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4.5 19.5h15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <rect x="6" y="11" width="3" height="6" rx="1" stroke="currentColor" strokeWidth="1.6" />
      <rect x="10.5" y="7" width="3" height="10" rx="1" stroke="currentColor" strokeWidth="1.6" />
      <rect x="15" y="9.5" width="3" height="7.5" rx="1" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  ),
  approvals: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8.5 12.2l2.4 2.4 4.6-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  alert: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 4.5 20 19H4L12 4.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M12 10v4M12 16.5h.01" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  complaint: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M8 4.5h6.5L18.5 8.5V19a1.5 1.5 0 0 1-1.5 1.5H8A1.5 1.5 0 0 1 6.5 19V6A1.5 1.5 0 0 1 8 4.5Z"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path d="M14.5 4.5V8.5h4M9.5 12.5h5M9.5 16h3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  register: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="5" y="3.5" width="14" height="17" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M9 8h6M9 12h6M9 16h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  billing: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3.5" y="6" width="17" height="12" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="12" cy="12" r="2.4" stroke="currentColor" strokeWidth="1.6" />
      <path d="M7 9.5h1.2M15.8 14.5H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  assets: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4.5 8.5 12 4.5l7.5 4v7l-7.5 4-7.5-4v-7Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M12 12.5V20.5M4.5 8.5 12 12.5l7.5-4" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  ),
  rooms: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4.5" y="4" width="15" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M9 8h2M13 8h2M9 12h2M13 12h2M9 16h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  org: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4.5 19.5h15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M6.5 19.5V9.5l5-4 5 4v10" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M10 12h1.5M12.5 12H14M10 15.5h1.5M12.5 15.5H14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  staff: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8.5" r="3.1" stroke="currentColor" strokeWidth="1.6" />
      <path d="M5.5 18.5c1-3 3.3-4.5 6.5-4.5s5.5 1.5 6.5 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  resources: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4.5 7.5h15M4.5 12h15M4.5 16.5h15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="8" cy="7.5" r="1.35" fill="currentColor" />
      <circle cx="12" cy="12" r="1.35" fill="currentColor" />
      <circle cx="16" cy="16.5" r="1.35" fill="currentColor" />
    </svg>
  ),
  workspace: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.8" stroke="currentColor" strokeWidth="1.6" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.8" stroke="currentColor" strokeWidth="1.6" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.8" stroke="currentColor" strokeWidth="1.6" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.8" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  ),
  settings: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M12 3.5v2.1M12 18.4v2.1M3.5 12h2.1M18.4 12h2.1M6.2 6.2l1.5 1.5M16.3 16.3l1.5 1.5M17.8 6.2l-1.5 1.5M7.7 16.3l-1.5 1.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  ),
  system: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 12h8M12 8v8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  finance: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 4.5v15M15.5 8.2c0-1.5-1.6-2.5-3.5-2.5S8.5 6.7 8.5 8.5s1.4 2.5 3.5 2.5 3.5.9 3.5 2.5-1.6 2.5-3.5 2.5-3.5-1-3.5-2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  booking: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3.5" y="5" width="17" height="15" rx="3" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 3.5v3M16 3.5v3M3.5 9.5h17M9 14h6M12 11.5v5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
}

function pathMatches(pathname: string, to: string, end?: boolean) {
  if (end) return pathname === to
  if (to === '/') return pathname === '/'
  return pathname === to || pathname.startsWith(`${to}/`)
}

function FlyoutMenu({
  entry,
  open,
  onOpen,
  onClose,
  onNavigate,
  pathname,
}: {
  entry: NavFlyout
  open: boolean
  onOpen: () => void
  onClose: () => void
  onNavigate: () => void
  pathname: string
}) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const closeTimer = useRef<number | null>(null)
  const leaves = flyoutLeaves(entry)
  const active = leaves.some((c) => pathMatches(pathname, c.to, c.end))
  const sections: NavFlyoutGroup[] = entry.groups?.length
    ? entry.groups
    : [{ label: entry.label, items: entry.children ?? [] }]

  const clearClose = () => {
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }

  const scheduleClose = () => {
    clearClose()
    closeTimer.current = window.setTimeout(() => onClose(), 120)
  }

  useEffect(() => () => clearClose(), [])

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    function onDoc(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) onClose()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDoc)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDoc)
    }
  }, [open, onClose])

  function renderLink(child: NavLeaf) {
    return (
      <NavLink
        key={child.to}
        to={child.to}
        end={child.end}
        role="menuitem"
        className={({ isActive }) => `rail-flyout-link ${isActive ? 'active' : ''}`}
        onClick={() => {
          onClose()
          onNavigate()
        }}
      >
        <span className="rail-flyout-icon">{child.icon}</span>
        <span>{child.label}</span>
      </NavLink>
    )
  }

  return (
    <div
      className={`rail-item-wrap ${open ? 'open' : ''} ${active ? 'active' : ''}`}
      ref={wrapRef}
      onMouseEnter={() => {
        clearClose()
        onOpen()
      }}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        className={`rail-btn ${active ? 'active' : ''} ${open ? 'open' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={entry.label}
        onClick={() => (open ? onClose() : onOpen())}
      >
        <span className="rail-icon">{entry.icon}</span>
        <span className="rail-flyout-dot" aria-hidden="true" />
      </button>
      <span className="rail-tooltip" role="tooltip">
        {entry.label}
      </span>
      {open ? (
        <div
          className={`rail-flyout ${entry.groups?.length ? 'rail-flyout-grouped' : ''}`}
          role="menu"
          aria-label={entry.label}
          onMouseEnter={clearClose}
          onMouseLeave={scheduleClose}
        >
          {entry.groups?.length ? (
            <div className="rail-flyout-head">{entry.label}</div>
          ) : null}
          {sections.map((section) =>
            section.items.length ? (
              <div key={section.label} className="rail-flyout-group">
                {entry.groups?.length ? (
                  <div className="rail-flyout-group-label">{section.label}</div>
                ) : (
                  <div className="rail-flyout-head">{section.label}</div>
                )}
                <div className="rail-flyout-list">{section.items.map(renderLink)}</div>
              </div>
            ) : null,
          )}
        </div>
      ) : null}
    </div>
  )
}

function RailLink({
  item,
  onNavigate,
}: {
  item: NavLeaf
  onNavigate: () => void
}) {
  return (
    <div className="rail-item-wrap">
      <NavLink
        to={item.to}
        end={item.end}
        className={({ isActive }) => `rail-btn ${isActive ? 'active' : ''}`}
        aria-label={item.label}
        onClick={onNavigate}
      >
        <span className="rail-icon">{item.icon}</span>
      </NavLink>
      <span className="rail-tooltip" role="tooltip">
        {item.label}
      </span>
    </div>
  )
}

function RailSection({
  label,
  entries,
  openFlyout,
  setOpenFlyout,
  onNavigate,
  pathname,
}: {
  label: string
  entries: NavEntry[]
  openFlyout: string | null
  setOpenFlyout: (id: string | null) => void
  onNavigate: () => void
  pathname: string
}) {
  if (!entries.length) return null

  return (
    <div className="rail-section" data-section={label}>
      <div className="rail-section-mark" aria-hidden="true" title={label} />
      <div className="rail-section-items" role="group" aria-label={label}>
        {entries.map((entry) =>
          isFlyout(entry) ? (
            <FlyoutMenu
              key={entry.id}
              entry={entry}
              open={openFlyout === entry.id}
              onOpen={() => setOpenFlyout(entry.id)}
              onClose={() => setOpenFlyout(null)}
              onNavigate={onNavigate}
              pathname={pathname}
            />
          ) : (
            <RailLink key={entry.to} item={entry} onNavigate={onNavigate} />
          ),
        )}
      </div>
    </div>
  )
}

export function AppLayout() {
  const { user, logout, isAuthenticated } = useAuth()
  const { theme, setTheme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [openFlyout, setOpenFlyout] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const initials = useMemo(() => initialsFromName(user?.username), [user?.username])
  const roles = useMemo(() => user?.roles?.join(' · ') || 'Staff', [user])
  const roleList = user?.roles ?? []

  const closeMobile = useCallback(() => setMobileOpen(false), [])

  const setFlyoutSafe = useCallback((id: string | null) => {
    setOpenFlyout(id)
  }, [])

  const mainEntries = useMemo((): NavEntry[] => {
    return [
      { to: '/calendar', label: 'Sessions', icon: I.calendar },
      { to: '/referrals', label: 'Referrals', icon: I.referrals },
      { to: '/', label: 'Dashboard', end: true, icon: I.dashboard },
    ]
  }, [])

  const operationsEntries = useMemo((): NavEntry[] => {
    const items: NavEntry[] = [
      { to: '/roster', label: 'Roster', icon: I.roster },
      { to: '/timesheets', label: 'Timesheets', icon: I.clock },
      { to: '/capacity', label: 'Capacity', icon: I.capacity },
    ]
    if (canReviewApprovals(roleList)) {
      items.push({ to: '/approvals', label: 'Approvals', icon: I.approvals })
    }
    return items
  }, [roleList])

  const controlEntries = useMemo((): NavEntry[] => {
    const items: NavEntry[] = [{ to: '/incidents', label: 'Incidents', icon: I.alert }]
    if (canAccessManagementAreas(roleList)) {
      items.push(
        { to: '/complaints', label: 'Complaints', icon: I.complaint },
        { to: '/registers', label: 'Registers', icon: I.register },
        { to: '/billing', label: 'Billing', icon: I.billing },
      )
    }
    return items
  }, [roleList])

  const workspaceEntries = useMemo((): NavEntry[] => {
    const groups: NavFlyoutGroup[] = []
    const resourceItems: NavLeaf[] = []

    if (canAccessManagementAreas(roleList)) {
      resourceItems.push(
        { to: '/assets', label: 'Assets', icon: I.assets },
        { to: '/rooms', label: 'Rooms', icon: I.rooms },
      )
    }
    if (canAccessOrganisations(roleList)) {
      resourceItems.push({ to: '/settings/organizations', label: 'Organisations', icon: I.org })
    }
    if (canAccessManagementAreas(roleList)) {
      resourceItems.push({ to: '/staff', label: 'Staff', icon: I.staff })
    }
    if (resourceItems.length) {
      groups.push({ label: 'Resources', items: resourceItems })
    }

    if (canAccessAdminSettings(roleList)) {
      groups.push({
        label: 'Settings',
        items: [
          { to: '/settings/system', label: 'System Settings', icon: I.system },
          { to: '/settings/finance', label: 'Finance Settings', icon: I.finance },
          { to: '/settings/booking-types', label: 'Booking Types', icon: I.booking },
          { to: '/settings/risk', label: 'Risk Assessment', icon: I.settings },
        ],
      })
    }

    if (!groups.length) return []

    return [
      {
        id: 'workspace',
        label: 'Work Space',
        icon: I.workspace,
        groups,
      },
    ]
  }, [roleList])

  useEffect(() => {
    if (isAuthenticated) saveLastRoute(location.pathname)
  }, [location.pathname, isAuthenticated])

  useEffect(() => {
    setOpenFlyout(null)
  }, [location.pathname])

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  async function onLogout() {
    setMenuOpen(false)
    await logout()
    navigate('/login')
  }

  return (
    <div className="app-shell rail">
      {mobileOpen ? <div className="mobile-overlay" onClick={closeMobile} /> : null}

      <aside className={`sidebar rail-sidebar ${mobileOpen ? 'open' : ''}`}>
        <div className="sidebar-inner rail-inner">
          <div className="sidebar-brand rail-brand">
            <div className="brand-mark" aria-hidden="true" title="Nexus">
              N
            </div>
          </div>

          <nav className="rail-nav" aria-label="Primary">
            <div className="rail-nav-top">
              <RailSection
                label="Main"
                entries={mainEntries}
                openFlyout={openFlyout}
                setOpenFlyout={setFlyoutSafe}
                onNavigate={closeMobile}
                pathname={location.pathname}
              />
              <RailSection
                label="Operations"
                entries={operationsEntries}
                openFlyout={openFlyout}
                setOpenFlyout={setFlyoutSafe}
                onNavigate={closeMobile}
                pathname={location.pathname}
              />
              <RailSection
                label="Control"
                entries={controlEntries}
                openFlyout={openFlyout}
                setOpenFlyout={setFlyoutSafe}
                onNavigate={closeMobile}
                pathname={location.pathname}
              />
            </div>

            {workspaceEntries.length ? (
              <div className="rail-nav-bottom">
                <div className="rail-divider" aria-hidden="true" />
                <RailSection
                  label="Workspace"
                  entries={workspaceEntries}
                  openFlyout={openFlyout}
                  setOpenFlyout={setFlyoutSafe}
                  onNavigate={closeMobile}
                  pathname={location.pathname}
                />
              </div>
            ) : null}
          </nav>

          <div className="sidebar-footer rail-footer">
            <div className="user-menu" ref={menuRef}>
              <button
                type="button"
                className="user-trigger rail-user-trigger"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label={user?.username || 'Account'}
                onClick={() => {
                  setOpenFlyout(null)
                  setMenuOpen((o) => !o)
                }}
              >
                <span className="user-avatar">{initials}</span>
              </button>

              {menuOpen ? (
                <div className="user-popover rail-user-popover" role="menu">
                  <div className="rail-user-meta">
                    <strong>{user?.username || 'User'}</strong>
                    <span>{roles}</span>
                  </div>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false)
                      navigate('/profile')
                    }}
                  >
                    Profile
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
                  >
                    {theme === 'light' ? 'Dark mode' : 'Light mode'}
                  </button>
                  <div className="popover-divider" />
                  <button type="button" role="menuitem" className="danger" onClick={() => void onLogout()}>
                    Sign out
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </aside>

      <div className="main-column">
        <header className="topbar">
          <div className="topbar-left">
            <button
              type="button"
              className="icon-btn mobile-only"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
            <div className="topbar-copy">
              <div className="eyebrow">Nexus workspace</div>
              <h1 className="topbar-title">Family Contact Centre</h1>
            </div>
          </div>
        </header>
        <main className="page">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
